import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  categories,
  categoryColors,
  categoryTotals,
  colorForName,
  cycleLabel,
  cycleLabelShort,
  daysUntil,
  fmtDate,
  fmtDateLong,
  formatDateOnly,
  getNextPaymentDate,
  isValidDateOnly,
  makeId,
  markForName,
  monthlyPaymentTrend,
  monthlyOf,
  type PaymentOccurrence,
  parseYenInput,
  paymentOccurrencesInRange,
  startOfLocalDay,
  todayInputValue,
  totalMonthly,
  upcomingPayments,
  yen,
} from "./lib/subscriptions";
import { loadState, saveState } from "./storage";
import type { BillingCycle, PersistedState, Reminder, Subscription, ThemeName } from "./types";

type Tab = "home" | "calendar" | "analytics";
type FormMode = { kind: "add" } | { kind: "edit"; subscription: Subscription };

const themes = {
  dark: {
    name: "dark",
    bg: "#0A0A0C",
    cardBg: "#141418",
    chipBg: "#16161B",
    statBg: "rgba(255,255,255,0.025)",
    line: "rgba(255,255,255,0.07)",
    heroBg: "linear-gradient(160deg, #1A1626 0%, #11111A 60%, #0F0F14 100%)",
    heroBorder: "rgba(124,92,255,0.25)",
    warnBg: "rgba(255,184,107,0.06)",
    warnBorder: "rgba(255,184,107,0.22)",
    fg: "#FAFAFA",
    fg2: "rgba(255,255,255,0.78)",
    fg3: "rgba(255,255,255,0.42)",
    navBg: "rgba(10,10,12,0.85)",
  },
  light: {
    name: "light",
    bg: "#F6F4F0",
    cardBg: "#FFFFFF",
    chipBg: "#FFFFFF",
    statBg: "#FFFFFF",
    line: "rgba(10,10,20,0.07)",
    heroBg: "linear-gradient(160deg, #ECE7FF 0%, #FFFFFF 60%, #FFFFFF 100%)",
    heroBorder: "rgba(124,92,255,0.25)",
    warnBg: "rgba(255,150,40,0.08)",
    warnBorder: "rgba(255,150,40,0.25)",
    fg: "#0A0A0C",
    fg2: "rgba(10,10,20,0.72)",
    fg3: "rgba(10,10,20,0.42)",
    navBg: "rgba(246,244,240,0.92)",
  },
};

type Theme = (typeof themes)[ThemeName];

const popularServices = [
  { name: "Netflix", color: "#E50914", mark: "N", category: "エンタメ" },
  { name: "Spotify", color: "#1ED760", mark: "S", category: "音楽" },
  { name: "ChatGPT Plus", color: "#10A37F", mark: "C", category: "AI / 仕事" },
  { name: "Adobe CC", color: "#FA0F00", mark: "A", category: "クリエイティブ" },
  { name: "Disney+", color: "#0E72E1", mark: "D", category: "エンタメ" },
  { name: "Microsoft 365", color: "#F25022", mark: "M", category: "AI / 仕事" },
];

export default function App() {
  const [state, setState] = useState<PersistedState>(() => {
    return loadState();
  });
  const [tab, setTab] = useState<Tab>("home");
  const [openSubId, setOpenSubId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const today = useToday();
  const didMount = useRef(false);

  useEffect(() => {
    document.documentElement.dataset.theme = state.settings.theme;
    const theme = themes[state.settings.theme];
    document.documentElement.style.setProperty("--app-bg", theme.bg);
    document.documentElement.style.setProperty("--card-bg", theme.cardBg);
    document.documentElement.style.setProperty("--fg", theme.fg);
    document.documentElement.style.setProperty("--fg-muted", theme.fg2);
    document.documentElement.style.setProperty("--fg-subtle", theme.fg3);
    document.documentElement.style.setProperty("--line", theme.line);
  }, [state.settings.theme]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    setSaveFailed(!saveState(state));
  }, [state]);

  const theme = themes[state.settings.theme];
  const openSubscription = state.subscriptions.find((sub) => sub.id === openSubId) ?? null;

  const upsertSubscription = (subscription: Subscription) => {
    setState((current) => {
      const exists = current.subscriptions.some((sub) => sub.id === subscription.id);
      return {
        ...current,
        subscriptions: exists
          ? current.subscriptions.map((sub) => (sub.id === subscription.id ? subscription : sub))
          : [...current.subscriptions, subscription],
      };
    });
    setOpenSubId(subscription.id);
    setFormMode(null);
  };

  const startAdd = () => {
    setOpenSubId(null);
    setFormMode({ kind: "add" });
  };

  const startEdit = (subscription: Subscription) => {
    setFormMode({ kind: "edit", subscription });
    setOpenSubId(null);
  };

  const deleteSubscription = (id: string) => {
    const target = state.subscriptions.find((sub) => sub.id === id);
    if (!target) return;
    if (!window.confirm(`${target.name} をアプリ内から削除しますか？`)) return;

    setState((current) => ({
      ...current,
      subscriptions: current.subscriptions.filter((sub) => sub.id !== id),
    }));
    setOpenSubId(null);
  };

  const toggleTheme = () => {
    setState((current) => ({
      ...current,
      settings: {
        ...current.settings,
        theme: current.settings.theme === "dark" ? "light" : "dark",
      },
    }));
  };

  return (
    <div className="app-shell" style={{ background: theme.bg, color: theme.fg }}>
      <main className="phone-free-app" aria-label="Subly サブスク管理">
        {saveFailed && (
          <div className="save-warning" role="status">
            ローカル保存に失敗しました。ブラウザの空き容量やプライベートモード設定を確認してください。
          </div>
        )}
        <div className="content-stack">
          {tab === "home" && (
            <HomeScreen
              subscriptions={state.subscriptions}
              theme={theme}
              today={today}
              onOpen={setOpenSubId}
              onAdd={startAdd}
              onTab={setTab}
              onToggleTheme={toggleTheme}
            />
          )}
          {tab === "calendar" && (
            <CalendarScreen subscriptions={state.subscriptions} theme={theme} today={today} onOpen={setOpenSubId} />
          )}
          {tab === "analytics" && (
            <AnalyticsScreen subscriptions={state.subscriptions} theme={theme} today={today} onOpen={setOpenSubId} />
          )}
        </div>

        <BottomNav tab={tab} setTab={setTab} theme={theme} onAdd={startAdd} />
      </main>

      {formMode && (
        <SubscriptionForm
          mode={formMode}
          theme={theme}
          onCancel={() => setFormMode(null)}
          onSubmit={upsertSubscription}
        />
      )}

      {openSubscription && (
        <DetailSheet
          subscription={openSubscription}
          theme={theme}
          today={today}
          onClose={() => setOpenSubId(null)}
          onEdit={() => startEdit(openSubscription)}
          onDelete={() => deleteSubscription(openSubscription.id)}
        />
      )}
    </div>
  );
}

function HomeScreen({
  subscriptions,
  theme,
  today,
  onOpen,
  onAdd,
  onTab,
  onToggleTheme,
}: {
  subscriptions: Subscription[];
  theme: Theme;
  today: Date;
  onOpen: (id: string) => void;
  onAdd: () => void;
  onTab: (tab: Tab) => void;
  onToggleTheme: () => void;
}) {
  const upcoming = upcomingPayments(subscriptions, today, 60);
  const weekCount = upcoming.filter((occurrence) => daysUntil(occurrence.date, today) <= 7).length;
  const monthly = totalMonthly(subscriptions);
  const sortedAll = [...subscriptions].sort((a, b) => getNextPaymentDate(a, today).localeCompare(getNextPaymentDate(b, today)));
  const highCost = subscriptions.filter((sub) => monthlyOf(sub) >= 5000);

  return (
    <section className="screen scrollable">
      <div className="screen-header">
        <div>
          <div className="kicker">SUBLY · {today.toLocaleDateString("ja-JP", { month: "short", day: "numeric" })}</div>
          <h1>サブスク管理</h1>
        </div>
        <div className="header-actions">
          <IconButton label="テーマ切替" theme={theme} onClick={onToggleTheme}>
            <Icon name="settings" />
          </IconButton>
        </div>
      </div>

      <div className="hero-card" style={{ background: theme.heroBg, borderColor: theme.heroBorder }}>
        <div className="hero-glow" />
        <div className="spread">
          <span className="kicker">今月の合計 / Monthly Total</span>
          <span className="trend">Live</span>
        </div>
        <div className="hero-total">{yen(monthly)}</div>
        <div className="muted-line">
          年間換算 <span>{yen(monthly * 12)}</span>
        </div>
        <div className="mini-grid">
          <MiniStat label="登録中" labelEn="Active" value={subscriptions.length} suffix="件" theme={theme} />
          <MiniStat label="今週支払い" labelEn="This week" value={weekCount} suffix="件" theme={theme} />
          <MiniStat label="見直し候補" labelEn="Review" value={highCost.length} suffix="件" theme={theme} highlight />
        </div>
      </div>

      {subscriptions.length === 0 ? (
        <EmptyState theme={theme} onAdd={onAdd} />
      ) : (
        <>
          {highCost.length > 0 && (
            <button className="review-banner" style={{ background: theme.warnBg, borderColor: theme.warnBorder }} onClick={() => onTab("analytics")}>
              <span className="warn-mark">
                <Icon name="warn" />
              </span>
              <span>
                <strong>{highCost.length}件の高額サブスクがあります</strong>
                <small>月額¥5,000以上の項目を分析で確認できます</small>
              </span>
              <Icon name="chevron" />
            </button>
          )}

          <SectionLabel jp="次回の支払い" en="Upcoming" theme={theme} />
          <div className="list">
            {upcoming.slice(0, 6).map((occurrence) => (
              <UpcomingRow key={occurrence.id} occurrence={occurrence} theme={theme} today={today} onClick={() => onOpen(occurrence.subscription.id)} />
            ))}
            {upcoming.length === 0 && <SmallEmpty theme={theme}>今後60日以内の支払いはありません</SmallEmpty>}
          </div>

          <SectionLabel jp="すべてのサブスクリプション" en={`All · ${subscriptions.length}`} theme={theme} />
          <div className="list compact">
            {sortedAll.map((sub) => (
              <SubRow key={sub.id} subscription={sub} theme={theme} today={today} onClick={() => onOpen(sub.id)} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function CalendarScreen({
  subscriptions,
  theme,
  today,
  onOpen,
}: {
  subscriptions: Subscription[];
  theme: Theme;
  today: Date;
  onOpen: (id: string) => void;
}) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const year = today.getFullYear();
  const month = today.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<number | null> = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const monthOccurrences = paymentOccurrencesInRange(subscriptions, monthStart, monthEnd);
  const paymentsByDay = new Map<number, PaymentOccurrence[]>();

  monthOccurrences.forEach((occurrence) => {
    const date = new Date(`${occurrence.date}T00:00:00`);
    const list = paymentsByDay.get(date.getDate()) ?? [];
    paymentsByDay.set(date.getDate(), [...list, occurrence]);
  });

  const monthTotal = monthOccurrences.reduce((sum, occurrence) => sum + occurrence.amount, 0);
  const upcoming = upcomingPayments(subscriptions, today, 60);
  const selectedOccurrences = selectedDay ? monthOccurrences.filter((occurrence) => occurrence.date === selectedDay) : [];

  return (
    <section className="screen scrollable">
      <div className="screen-header">
        <div>
          <div className="kicker">Calendar</div>
          <h1>{year}年 {month + 1}月</h1>
        </div>
      </div>

      <div className="summary-card" style={{ background: theme.cardBg, borderColor: theme.line }}>
        <div>
          <div className="kicker">今月支払い予定</div>
          <div className="summary-price">{yen(monthTotal)}</div>
        </div>
        <span className="count-pill">{monthOccurrences.length} payments</span>
      </div>

      <div className="calendar-board">
        {["日", "月", "火", "水", "木", "金", "土"].map((day, index) => (
          <div key={day} className={`weekday weekday-${index}`}>{day}</div>
        ))}
        {cells.map((day, index) => {
          if (day === null) return <div key={`blank-${index}`} />;
          const payments = paymentsByDay.get(day) ?? [];
          const isToday = day === today.getDate();
          const date = formatDateOnly(new Date(year, month, day));
          const className = `calendar-cell ${isToday ? "today" : ""} ${payments.length ? "has-payments" : ""}`;
          if (payments.length === 0) {
            return (
              <div key={day} className={className} aria-label={`${day}日 支払いなし`}>
                <span>{day}</span>
              </div>
            );
          }
          return (
            <button
              key={day}
              className={className}
              type="button"
              aria-label={calendarPaymentLabel(date, payments)}
              onClick={() => {
                if (payments.length === 1) {
                  setSelectedDay(null);
                  onOpen(payments[0].subscription.id);
                } else {
                  setSelectedDay(date);
                }
              }}
            >
              <span>{day}</span>
              <span className="dot-row">
                {payments.slice(0, 3).map((occurrence) => (
                  <i key={occurrence.id} style={{ background: occurrence.subscription.color }} />
                ))}
                {payments.length > 3 && <em>+{payments.length - 3}</em>}
              </span>
            </button>
          );
        })}
      </div>

      <SectionLabel jp="今後の支払い" en="Next 60 days" theme={theme} />
      <div className="timeline">
        {upcoming.map((occurrence, index) => {
          const diff = daysUntil(occurrence.date, today);
          const showBreak = index === 0 || occurrence.date !== upcoming[index - 1].date;
          return (
            <div key={occurrence.id}>
              {showBreak && (
                <div className="date-break">
                  <strong>{fmtDate(occurrence.date)}</strong>
                  <span>{diff === 0 ? "TODAY" : `IN ${diff}D`}</span>
                  <i />
                </div>
              )}
              <SubTimelineRow occurrence={occurrence} theme={theme} onClick={() => onOpen(occurrence.subscription.id)} />
            </div>
          );
        })}
        {upcoming.length === 0 && <SmallEmpty theme={theme}>今後60日以内の支払いはありません</SmallEmpty>}
      </div>
      {selectedOccurrences.length > 1 && (
        <DayPaymentsSheet
          date={selectedDay!}
          occurrences={selectedOccurrences}
          theme={theme}
          onClose={() => setSelectedDay(null)}
          onOpen={onOpen}
        />
      )}
    </section>
  );
}

function AnalyticsScreen({
  subscriptions,
  theme,
  today,
  onOpen,
}: {
  subscriptions: Subscription[];
  theme: Theme;
  today: Date;
  onOpen: (id: string) => void;
}) {
  const cats = categoryTotals(subscriptions);
  const totalCat = cats.reduce((sum, cat) => sum + cat.value, 0);
  const top = [...subscriptions].sort((a, b) => monthlyOf(b) - monthlyOf(a)).slice(0, 5);
  const trend = useMemo(() => monthlyPaymentTrend(subscriptions, today, 6), [subscriptions, today]);
  const currentMonthPlanned = trend[trend.length - 1]?.value ?? 0;
  const max = Math.max(...trend.map((item) => item.value), 1);

  return (
    <section className="screen scrollable">
      <div className="screen-header">
        <div>
          <div className="kicker">Insights</div>
          <h1>支出の分析</h1>
        </div>
      </div>

      {subscriptions.length === 0 ? (
        <SmallEmpty theme={theme}>サブスクを追加すると分析が表示されます</SmallEmpty>
      ) : (
        <>
          <div className="chart-card" style={{ background: theme.cardBg, borderColor: theme.line }}>
            <div className="spread">
              <div>
                <div className="kicker">月別支払い予定 · 6ヶ月</div>
                <div className="summary-price">{yen(currentMonthPlanned)}</div>
                <div className="chart-note">支払い予定額ベース</div>
              </div>
              <span className="count-pill">6M</span>
            </div>
            <div className="bar-chart">
              {trend.map((item, index) => {
                const isCurrent = index === trend.length - 1;
                return (
                  <div key={item.label} className="bar-item">
                    <span>{item.value > 0 ? `${Math.round(item.value / 1000)}k` : "0"}</span>
                    <i style={{ height: `${Math.max(8, (item.value / max) * 100)}%` }} className={isCurrent ? "current" : ""} />
                    <em>{item.label}</em>
                  </div>
                );
              })}
            </div>
          </div>

          <SectionLabel jp="カテゴリ別" en="By category" theme={theme} />
          <div className="category-card" style={{ background: theme.cardBg, borderColor: theme.line }}>
            <div className="stacked-bar">
              {cats.map((cat) => (
                <span key={cat.name} style={{ flex: cat.value, background: categoryColors[cat.name] ?? "#7C5CFF" }} />
              ))}
            </div>
            {cats.map((cat) => (
              <div key={cat.name} className="category-row">
                <i style={{ background: categoryColors[cat.name] ?? "#7C5CFF" }} />
                <span>{cat.name}</span>
                <strong>{yen(cat.value)}</strong>
                <em>{Math.round((cat.value / totalCat) * 100)}%</em>
              </div>
            ))}
          </div>

          <SectionLabel jp="支出トップ5" en="Top services" theme={theme} />
          <div className="list">
            {top.map((sub, index) => (
              <button key={sub.id} className="top-row" style={{ background: theme.cardBg, borderColor: theme.line }} onClick={() => onOpen(sub.id)}>
                <span className="rank">0{index + 1}</span>
                <SubMark subscription={sub} size={34} />
                <span className="row-main">
                  <strong>{sub.name}</strong>
                  <small>{sub.category}</small>
                </span>
                <span className="money">{yen(monthlyOf(sub))}</span>
              </button>
            ))}
          </div>

          <SectionLabel jp="インサイト" en="Insights" theme={theme} />
          <div className="insight-list">
            <InsightCard theme={theme} accent="#FFB86B" title="高額な項目を定期的に確認しましょう" body="月額換算が大きいサービスほど、プラン変更の効果が出やすくなります。" />
            <InsightCard theme={theme} accent="#7CE0B8" title="支払い日が近いものを先に確認" body="カレンダー画面で60日以内の支払いをまとめて確認できます。" />
          </div>
        </>
      )}
    </section>
  );
}

function SubscriptionForm({
  mode,
  theme,
  onCancel,
  onSubmit,
}: {
  mode: FormMode;
  theme: Theme;
  onCancel: () => void;
  onSubmit: (subscription: Subscription) => void;
}) {
  const editing = mode.kind === "edit";
  const source = editing ? mode.subscription : null;
  const [name, setName] = useState(source?.name ?? "");
  const [price, setPrice] = useState(source ? String(source.price) : "");
  const [cycle, setCycle] = useState<BillingCycle>(source?.cycle ?? "monthly");
  const [category, setCategory] = useState(source?.category ?? categories[0]);
  const [nextDate, setNextDate] = useState(source?.nextDate ?? todayInputValue());
  const [method, setMethod] = useState(source?.method ?? "");
  const [reminder, setReminder] = useState<Reminder>(source?.reminder ?? "3d");
  const [error, setError] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const cleanName = name.trim();
    const cleanPrice = parseYenInput(price);

    if (!cleanName) {
      setError("サービス名を入力してください");
      return;
    }
    if (cleanPrice === null) {
      setError("金額は1円以上の整数で入力してください");
      return;
    }
    if (!isValidDateOnly(nextDate)) {
      setError("次回支払日は実在する日付で入力してください");
      return;
    }

    const now = new Date().toISOString();
    const matched = popularServices.find((item) => item.name.toLowerCase() === cleanName.toLowerCase());
    const nameChanged = !source || source.name.trim().toLowerCase() !== cleanName.toLowerCase();
    onSubmit({
      id: source?.id ?? makeId(),
      name: cleanName,
      price: Math.round(cleanPrice),
      cycle,
      category,
      nextDate,
      method: method.trim() || "未設定",
      reminder,
      color: !nameChanged && source ? source.color : matched?.color ?? colorForName(cleanName),
      mark: !nameChanged && source ? source.mark : matched?.mark ?? markForName(cleanName),
      createdAt: source?.createdAt ?? now,
      updatedAt: now,
    });
  };

  const applyPopular = (item: (typeof popularServices)[number]) => {
    setName(item.name);
    setCategory(item.category);
  };

  return (
    <div className="modal-layer full" role="dialog" aria-modal="true" aria-label={editing ? "サブスク編集" : "新規追加"}>
      <form className="form-screen scrollable" style={{ background: theme.bg, color: theme.fg }} onSubmit={submit}>
        <div className="screen-header">
          <IconButton label="閉じる" theme={theme} onClick={onCancel} type="button">
            <Icon name="close" />
          </IconButton>
          <h1>{editing ? "編集" : "新規追加"}</h1>
          <span className="header-spacer" />
        </div>

        <div className="intro">
          <div className="kicker">{editing ? "Edit subscription" : "Add subscription"}</div>
          <h2>{editing ? "内容を更新します" : "サービスを登録します"}</h2>
        </div>

        {!editing && (
          <>
            <Field label="サービス検索" labelEn="Search" theme={theme} htmlFor="service-search">
              <div className="search-field" style={{ background: theme.cardBg, borderColor: theme.line }}>
                <Icon name="search" />
                <input id="service-search" value={name} onChange={(event) => setName(event.target.value)} placeholder="サービス名を検索 / Search service" />
              </div>
            </Field>

            <SectionLabel jp="人気のサービス" en="Popular" theme={theme} />
            <div className="chip-scroller">
              {popularServices.map((item) => (
                <button key={item.name} className="service-chip" type="button" style={{ background: theme.chipBg, borderColor: theme.line }} onClick={() => applyPopular(item)}>
                  <span style={{ background: item.color }}>{item.mark}</span>
                  {item.name}
                </button>
              ))}
            </div>
          </>
        )}

        <SectionLabel jp="詳細情報" en="Details" theme={theme} />
        <div className="form-grid">
          <Field label="サービス名" labelEn="Name" theme={theme} htmlFor="service-name">
            <input id="service-name" className="field-input" value={name} onChange={(event) => setName(event.target.value)} placeholder="例: Netflix" />
          </Field>

          <div className="two-col">
            <Field label="金額" labelEn="Price (JPY)" theme={theme} htmlFor="subscription-price">
              <input id="subscription-price" className="field-input mono" value={price} onChange={(event) => setPrice(event.target.value)} inputMode="numeric" pattern="[0-9,]*" placeholder="1980" />
            </Field>
            <Field label="周期" labelEn="Cycle" theme={theme}>
              <Segmented
                options={[
                  { value: "monthly", label: "月" },
                  { value: "yearly", label: "年" },
                  { value: "weekly", label: "週" },
                ]}
                value={cycle}
                onChange={(value) => setCycle(value as BillingCycle)}
                ariaLabel="周期"
                theme={theme}
              />
            </Field>
          </div>

          <Field label="カテゴリ" labelEn="Category" theme={theme}>
            <div className="category-chips" role="group" aria-label="カテゴリ">
              {categories.map((item) => (
                <button key={item} type="button" className={category === item ? "selected" : ""} aria-pressed={category === item} onClick={() => setCategory(item)}>
                  {item}
                </button>
              ))}
            </div>
          </Field>

          <Field label={editing ? "支払い基準日" : "次回支払日"} labelEn={editing ? "Billing anchor" : "Next charge"} theme={theme} htmlFor="next-date">
            <input id="next-date" className="field-input mono" type="date" value={nextDate} onChange={(event) => setNextDate(event.target.value)} />
          </Field>

          <Field label="支払い方法" labelEn="Payment method" theme={theme} htmlFor="payment-method">
            <input id="payment-method" className="field-input mono" value={method} onChange={(event) => setMethod(event.target.value)} placeholder="例: VISA •• 4821" />
          </Field>

          <Field label="リマインダー" labelEn="Reminder" theme={theme}>
            <Segmented
              options={[
                { value: "1d", label: "1日前" },
                { value: "3d", label: "3日前" },
                { value: "off", label: "オフ" },
              ]}
              value={reminder}
              onChange={(value) => setReminder(value as Reminder)}
              ariaLabel="リマインダー"
              theme={theme}
            />
          </Field>
        </div>

        {error && <p className="form-error">{error}</p>}

        <button className="primary-action" type="submit">
          {editing ? "更新する · Save changes" : "登録する · Add subscription"}
        </button>
      </form>
    </div>
  );
}

function DetailSheet({
  subscription,
  theme,
  today,
  onClose,
  onEdit,
  onDelete,
}: {
  subscription: Subscription;
  theme: Theme;
  today: Date;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const nextPaymentDate = getNextPaymentDate(subscription, today);
  return (
    <div className="modal-layer sheet-layer" role="dialog" aria-modal="true" aria-label={`${subscription.name} の詳細`} onClick={onClose}>
      <div className="detail-sheet scrollable" style={{ background: theme.bg, color: theme.fg, borderColor: theme.line }} onClick={(event) => event.stopPropagation()}>
        <div className="drag-handle" />
        <div className="detail-hero">
          <SubMark subscription={subscription} size={58} />
          <div>
            <h2>{subscription.name}</h2>
            <p>{subscription.category} · {cycleLabel(subscription.cycle)}</p>
          </div>
          <IconButton label="閉じる" theme={theme} onClick={onClose}>
            <Icon name="close" />
          </IconButton>
        </div>

        <div className="price-card" style={{ background: theme.cardBg, borderColor: theme.line }}>
          <div className="spread kicker">
            <span>請求額</span>
            <span>月換算</span>
          </div>
          <div className="spread">
            <strong>{yen(subscription.price)}</strong>
            <em>{yen(monthlyOf(subscription))}</em>
          </div>
        </div>

        <div className="fact-grid">
          <Fact label="次回支払日" value={fmtDateLong(nextPaymentDate)} sub={relativeDateLabel(nextPaymentDate, today)} theme={theme} />
          <Fact label="周期" value={cycleLabel(subscription.cycle)} sub={cycleLabelShort(subscription.cycle)} theme={theme} />
          <Fact label="支払い方法" value={subscription.method} theme={theme} />
          <Fact label="カテゴリ" value={subscription.category} theme={theme} />
        </div>

        <SectionLabel jp="支払い履歴" en="Recent payments" theme={theme} />
        <SmallEmpty theme={theme}>履歴は今後の拡張用です。現在は次回支払いを管理します。</SmallEmpty>

        <div className="detail-actions">
          <button type="button" className="secondary-action" onClick={onEdit}>編集</button>
          <button type="button" className="danger-action" onClick={onDelete}>削除する</button>
        </div>
      </div>
    </div>
  );
}

function BottomNav({ tab, setTab, theme, onAdd }: { tab: Tab; setTab: (tab: Tab) => void; theme: Theme; onAdd: () => void }) {
  return (
    <nav className="bottom-nav" style={{ background: theme.navBg, borderColor: theme.line }} aria-label="メインナビゲーション">
      <NavButton active={tab === "home"} icon="home" label="Home" onClick={() => setTab("home")} />
      <NavButton active={tab === "calendar"} icon="calendar" label="Calendar" onClick={() => setTab("calendar")} />
      <button type="button" className="add-nav" aria-label="追加" onClick={onAdd}>
        <Icon name="plus" />
      </button>
      <NavButton active={tab === "analytics"} icon="chart" label="Analytics" onClick={() => setTab("analytics")} />
    </nav>
  );
}

function NavButton({ active, icon, label, onClick }: { active: boolean; icon: IconName; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`nav-button ${active ? "active" : ""}`} onClick={onClick}>
      <Icon name={icon} />
      <span>{label}</span>
    </button>
  );
}

function MiniStat({ label, labelEn, value, suffix, highlight, theme }: { label: string; labelEn: string; value: number; suffix: string; highlight?: boolean; theme: Theme }) {
  return (
    <div className="mini-stat" style={{ background: theme.statBg, borderColor: theme.line }}>
      <span>{labelEn}</span>
      <strong className={highlight ? "highlight" : ""}>{value}<small>{suffix}</small></strong>
      <em>{label}</em>
    </div>
  );
}

function EmptyState({ theme, onAdd }: { theme: Theme; onAdd: () => void }) {
  return (
    <div className="empty-state" style={{ background: theme.cardBg, borderColor: theme.line }}>
      <div className="empty-icon">
        <Icon name="plus" />
      </div>
      <h2>まだサブスクがありません</h2>
      <p>追加ボタンからサービス名、金額、支払日を登録すると、合計・カレンダー・分析が自動で更新されます。</p>
      <button type="button" className="primary-action" onClick={onAdd}>最初のサブスクを追加</button>
    </div>
  );
}

function UpcomingRow({ occurrence, theme, today, onClick }: { occurrence: PaymentOccurrence; theme: Theme; today: Date; onClick: () => void }) {
  const { subscription } = occurrence;
  const reminder = reminderBadge(subscription, occurrence.date, today);
  return (
    <button type="button" className="upcoming-row" style={{ background: theme.cardBg, borderColor: theme.line }} onClick={onClick}>
      <SubMark subscription={subscription} size={40} />
      <span className="row-main">
        <strong>{subscription.name}</strong>
        <small>
          {fmtDateLong(occurrence.date)} · {relativeDateLabel(occurrence.date, today)}
          {reminder && <span className="reminder-badge">{reminder}</span>}
        </small>
      </span>
      <span className="row-price">
        <strong>{yen(occurrence.amount)}</strong>
        <small>{cycleLabelShort(subscription.cycle)}</small>
      </span>
    </button>
  );
}

function SubRow({ subscription, theme, today, onClick }: { subscription: Subscription; theme: Theme; today: Date; onClick: () => void }) {
  const nextPaymentDate = getNextPaymentDate(subscription, today);
  return (
    <button type="button" className="sub-row" onClick={onClick}>
      <SubMark subscription={subscription} size={36} />
      <span className="row-main">
        <strong>{subscription.name}</strong>
        <small>{subscription.category} · {subscription.method}</small>
      </span>
      <span className="row-price">
        <strong style={{ color: theme.fg2 }}>{yen(subscription.price)}</strong>
        <small>{fmtDate(nextPaymentDate)}</small>
      </span>
    </button>
  );
}

function SubTimelineRow({ occurrence, theme, onClick }: { occurrence: PaymentOccurrence; theme: Theme; onClick: () => void }) {
  const { subscription } = occurrence;
  return (
    <button type="button" className="timeline-row" style={{ background: theme.cardBg, borderColor: theme.line }} onClick={onClick}>
      <SubMark subscription={subscription} size={34} />
      <span className="row-main">
        <strong>{subscription.name}</strong>
        <small>{subscription.method}</small>
      </span>
      <span className="money">{yen(occurrence.amount)}</span>
    </button>
  );
}

function SubMark({ subscription, size }: { subscription: Subscription; size: number }) {
  const darkText = ["#FFFFFF", "#7BB7FF", "#FFD166", "#F8E11A"].includes(subscription.color);
  return (
    <span className="sub-mark" style={{ width: size, height: size, borderRadius: size * 0.28, background: subscription.color, color: darkText ? "#0A0A0A" : "#FFFFFF", fontSize: size * 0.45 }}>
      {subscription.mark}
    </span>
  );
}

function SectionLabel({ jp, en, theme }: { jp: string; en: string; theme: Theme }) {
  return (
    <div className="section-label">
      <strong>{jp}</strong>
      <span>{en}</span>
      <i style={{ background: theme.line }} />
    </div>
  );
}

function Field({ label, labelEn, theme, htmlFor, children }: { label: string; labelEn: string; theme: Theme; htmlFor?: string; children: ReactNode }) {
  const LabelTag = htmlFor ? "label" : "span";
  return (
    <div className="field">
      <LabelTag className="field-label" {...(htmlFor ? { htmlFor } : {})}>
        <strong style={{ color: theme.fg2 }}>{label}</strong>
        <em>{labelEn}</em>
      </LabelTag>
      {children}
    </div>
  );
}

function Segmented({ options, value, onChange, ariaLabel, theme }: { options: Array<{ value: string; label: string }>; value: string; onChange: (value: string) => void; ariaLabel: string; theme: Theme }) {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel} style={{ background: theme.chipBg, borderColor: theme.line }}>
      {options.map((option) => (
        <button key={option.value} type="button" className={value === option.value ? "selected" : ""} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Fact({ label, value, sub, theme }: { label: string; value: string; sub?: string; theme: Theme }) {
  return (
    <div className="fact" style={{ background: theme.cardBg, borderColor: theme.line }}>
      <span>{label}</span>
      <strong>{value}</strong>
      {sub && <em>{sub}</em>}
    </div>
  );
}

function SmallEmpty({ theme, children }: { theme: Theme; children: ReactNode }) {
  return (
    <div className="small-empty" style={{ background: theme.cardBg, borderColor: theme.line }}>
      {children}
    </div>
  );
}

function InsightCard({ theme, accent, title, body }: { theme: Theme; accent: string; title: string; body: string }) {
  return (
    <div className="insight-card" style={{ background: theme.cardBg, borderColor: theme.line }}>
      <i style={{ background: accent }} />
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

function IconButton({ label, theme, children, onClick, type = "button" }: { label: string; theme: Theme; children: ReactNode; onClick?: () => void; type?: "button" | "submit" }) {
  return (
    <button type={type} className="icon-button" aria-label={label} style={{ background: theme.chipBg, borderColor: theme.line }} onClick={onClick}>
      {children}
    </button>
  );
}

function DayPaymentsSheet({
  date,
  occurrences,
  theme,
  onClose,
  onOpen,
}: {
  date: string;
  occurrences: PaymentOccurrence[];
  theme: Theme;
  onClose: () => void;
  onOpen: (id: string) => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = `day-sheet-${date}`;

  useEffect(() => {
    closeRef.current?.focus();
  }, [date]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="day-sheet"
      style={{ background: theme.cardBg, borderColor: theme.line }}
      role="dialog"
      aria-labelledby={titleId}
    >
      <div className="spread">
        <strong id={titleId}>{fmtDateLong(date)} の支払い</strong>
        <button type="button" ref={closeRef} onClick={onClose}>閉じる</button>
      </div>
      <div className="list">
        {occurrences.map((occurrence) => (
          <SubTimelineRow
            key={occurrence.id}
            occurrence={occurrence}
            theme={theme}
            onClick={() => {
              onClose();
              onOpen(occurrence.subscription.id);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function useToday() {
  const [today, setToday] = useState(() => startOfLocalDay(new Date()));

  useEffect(() => {
    const refresh = () => {
      setToday((current) => {
        const next = startOfLocalDay(new Date());
        return next.getTime() === current.getTime() ? current : next;
      });
    };

    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  return today;
}

function relativeDateLabel(isoDate: string, today: Date) {
  const diff = daysUntil(isoDate, today);
  if (diff === 0) return "今日";
  if (diff > 0) return `${diff}日後`;
  return `${Math.abs(diff)}日前`;
}

function reminderBadge(subscription: Subscription, date: string, today: Date) {
  if (subscription.reminder === "off") return null;
  const diff = daysUntil(date, today);
  const threshold = subscription.reminder === "1d" ? 1 : 3;
  if (diff < 0 || diff > threshold) return null;
  return `${diff === 0 ? "今日" : `あと${diff}日`} · リマインダー`;
}

function calendarPaymentLabel(date: string, payments: PaymentOccurrence[]) {
  const summary = payments
    .slice(0, 2)
    .map((occurrence) => `${occurrence.subscription.name} ${yen(occurrence.amount)}`)
    .join("、");
  const rest = payments.length > 2 ? `、ほか${payments.length - 2}件` : "";
  return `${fmtDateLong(date)} ${summary}${rest} の支払い`;
}

type IconName = "home" | "calendar" | "plus" | "chart" | "settings" | "search" | "close" | "chevron" | "warn";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    home: (
      <>
        <path d="M3 11.5L12 4l9 7.5" />
        <path d="M5 10.5V20h14v-9.5" />
        <path d="M10 20v-5h4v5" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M3 10h18M8 3v4M16 3v4" />
        <circle cx="8" cy="15" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="12" cy="15" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="16" cy="15" r="0.9" fill="currentColor" stroke="none" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    chart: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.5 12c0-.6-.06-1.18-.18-1.74l2.06-1.6-2-3.46-2.43.97a7.5 7.5 0 00-3-1.74L13.5 2h-4l-.45 2.43a7.5 7.5 0 00-3 1.74L3.62 5.2l-2 3.46 2.06 1.6c-.12.56-.18 1.14-.18 1.74s.06 1.18.18 1.74l-2.06 1.6 2 3.46 2.43-.97a7.5 7.5 0 003 1.74L9.5 22h4l.45-2.43a7.5 7.5 0 003-1.74l2.43.97 2-3.46-2.06-1.6c.12-.56.18-1.14.18-1.74z" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l4 4" />
      </>
    ),
    close: <path d="M6 6l12 12M6 18L18 6" />,
    chevron: <path d="M9 6l6 6-6 6" />,
    warn: (
      <>
        <path d="M12 4l9.5 16.5h-19L12 4z" />
        <path d="M12 10v5" />
        <circle cx="12" cy="18" r="0.9" fill="currentColor" stroke="none" />
      </>
    ),
  };

  return (
    <svg className="icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
